import { Component, Inject, ViewEncapsulation, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OverlayRef } from '@angular/cdk/overlay';

@Component({
  selector: 'app-law-details-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './law-details-modal.component.html',
  styleUrls: ['./law-details-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LawDetailsModalComponent {
  data: any;
  isLoading = false;
  hasError = false;
  errorMessage = '';

  constructor(
    private overlayRef: OverlayRef,
    @Inject('LAW_DATA') lawData: any,
    @Optional() @Inject('IS_LOADING') isLoadingParam?: boolean
  ) {
    this.data = lawData;

    // 設定載入狀態
    if (isLoadingParam !== undefined) {
      this.isLoading = isLoadingParam;
    } else {
      // 檢查是否有法條內容，如果沒有則顯示載入狀態
      this.isLoading = !this.hasLawArticles();
    }
  }

  close(): void {
    this.overlayRef.dispose();
  }

  onBackgroundClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  formatArticleContent(content: string): string {
    if (!content) return '';
    return content.replace(/\n/g, '<br>');
  }

  // 檢查是否有法條內容
  hasLawArticles(): boolean {
    return this.data?.law_articles && Array.isArray(this.data.law_articles) && this.data.law_articles.length > 0;
  }

  getActualLawCount(): number {
    // 如果有 law_count 欄位，優先使用它（這是後端已經計算好的總條文數）
    if (this.data?.law_count !== undefined) {
      return this.data.law_count || 0;
    }

    // 如果沒有 law_count 但有 law_articles，則計算（排除章節）
    if (this.hasLawArticles()) {
      return this.data.law_articles.filter((article: any) =>
        !article.article_type || article.article_type !== 'C'
      ).length;
    }

    return 0;
  }

  getFilteredArticles(): any[] {
    if (!this.hasLawArticles()) {
      return [];
    }

    // 過濾掉 article_type 為 'C' 的章節
    return this.data.law_articles.filter((article: any) =>
      !article.article_type || article.article_type !== 'C'
    );
  }

  getSourceWebsiteName(lawUrl: string): string {
    if (!lawUrl) return '未設定';

    try {
      const url = new URL(lawUrl);
      const hostname = url.hostname;

      const hostMappings: Record<string, string> = {
        'law.moj.gov.tw': '全國法規資料庫',
        'laws.mol.gov.tw': '勞動部',
        'law.epa.gov.tw': '環境保護署',
        'laws.moj.gov.tw': '法務部',
        'mojlaw.moj.gov.tw': '法務部',
        'gazette.nat.gov.tw': '國家發展委員會',
        'osha.gov.tw': '職業安全衛生署',
        'law.coa.gov.tw': '農業委員會',
        'law.banking.gov.tw': '金融監督管理委員會',
        'laws.fsc.gov.tw': '金融監督管理委員會',
        'law.cdc.gov.tw': '疾病管制署',
        'law.nfa.gov.tw': '消防署',
        'law.dot.gov.tw': '交通部',
        'oaout.moenv.gov.tw': '環境部',
        'lawsearch.pcc.gov.tw': '公共工程委員會',
        'law.moea.gov.tw': '經濟部',
        'selaw.com.tw': '證卷暨期貨法令判解查詢系統',
        'moeaea.gov.tw': '經濟部能源署',
        'laws.taipei.gov.tw': '台北市法規查詢系統',
        'lawsearch.taichung.gov.tw': '台中市法規查詢系統',
        'law.nstc.gov.tw': '國科會',
        'law-out.mof.gov.tw': '財政部',
        'erss.nusc.gov.tw': '核能安全委員會',
        'outlaw.kcg.gov.tw': '高雄市法規查詢系統',
        'web.law.ntpc.gov.tw': '新北市法規查詢系統',
        'www.cdc.gov.tw':'衛服部'
      };

      if (hostname in hostMappings) {
        return hostMappings[hostname];
      }

      const hostnameWithoutWww = hostname.startsWith('www.') ? hostname.substring(4) : hostname;
      if (hostnameWithoutWww in hostMappings) {
        return hostMappings[hostnameWithoutWww];
      }

      return hostname;
    } catch (error) {
      console.error('解析URL時發生錯誤:', error);
      return '未設定';
    }
  }
}
